import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { api, getPlatformContext } from '../api.js'
import { hasPermission, session } from '../session.js'
import { auditServiceErrorKey, naftaPosAuditApi } from './auditService.js'
import { createJournalCache, defaultFilters, hasDraftChanges, journalScope, prepareFilters, toDraft } from './journalState.js'

export const journalCache = createJournalCache()
watch(() => journalScope(session), value => journalCache.setScope(value), { immediate: true, flush: 'sync' })
export const journalPermission = journal => journal === 'runs' ? 'report_runs.read' : 'audit.read'
export const scopeDenied = cause => [401, 403, 404].includes(cause?.status)
export function monitoringQuery(value) {
  return new URLSearchParams(Object.entries(value).filter(([, item]) => item != null && item !== '').map(([name, item]) => [name, String(item)]))
}

// Commit context only after principal and active selection guards pass.
export async function verifyMonitoringContext(isCurrent) {
  const principal = session.user?.id ?? session.user?.username
  const stationId = session.platformContext?.activeStationId
  const sourceId = session.platformContext?.activeNaftaPosInstanceId
  const [identity, context] = await Promise.all([api('/auth/me'), getPlatformContext()])
  if (!isCurrent()) return false
  if ((identity?.user?.id ?? identity?.user?.username) !== principal
    || context?.activeStationId !== stationId || context?.activeNaftaPosInstanceId !== sourceId
    || stationId != null && !context.stations?.some(station => station.id === stationId
      && (sourceId == null || station.naftaPosInstances?.some(source => source.id === sourceId && source.enabled)))) {
    throw Object.assign(new Error('Invalid context'), { status: 403 })
  }
  Object.assign(session.user, identity.user)
  session.platformContext = context
  return isCurrent()
}

export function useJournal(journal, { actor = '' } = {}) {
  const applied = reactive(defaultFilters(journal, new Date(), actor)), draft = reactive(toDraft(applied))
  const page = ref(1), pageSize = ref(25), searchDraft = ref(''), result = ref(null)
  const loading = ref(true), refreshing = ref(false), error = ref(''), validation = ref(''), stale = ref(false), panelOpen = ref(false), ready = ref(false)
  const options = reactive({ actor: optionState(), template: optionState() })
  let owner = journalCache.ticket(), generation = 0, disposed = false
  let verified = false
  const current = token => !disposed && token === generation && journalCache.current(owner)
  const query = () => ({ applied: { ...applied }, page: page.value, pageSize: pageSize.value })
  const pending = computed(() => hasDraftChanges(journal, draft, applied))
  const rows = computed(() => result.value?.[journal === 'runs' ? 'runs' : 'events'] ?? [])
  const total = computed(() => result.value?.total ?? 0)
  const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
  const sources = computed(() => session.platformContext?.stations?.find(station => station.id === session.platformContext.activeStationId)?.naftaPosInstances?.filter(source => source.enabled) ?? [])
  function capture() {
    if (verified && ready.value) journalCache.savePreferences(journal, owner, { ...query(), draft, searchDraft: searchDraft.value })
  }
  function clearDenied() {
    journalCache.clear(); generation++; verified = false; ready.value = false
    result.value = null; stale.value = false; panelOpen.value = false
    Object.assign(options, { actor: optionState(), template: optionState() })
  }
  async function load(number = page.value) {
    if (!ready.value || !hasPermission(journalPermission(journal))) return
    const token = ++generation
    page.value = number; refreshing.value = true; error.value = ''; capture()
    const descriptor = query()
    const params = monitoringQuery({ ...descriptor.applied, stationId: session.platformContext?.activeStationId, page: number, pageSize: pageSize.value })
    try {
      const payload = await (journal === 'runs' ? api : naftaPosAuditApi)(`${journal === 'runs' ? '/admin/report-runs' : '/admin/audit-events'}?${params}`)
      if (!current(token)) return
      result.value = payload; stale.value = false
      journalCache.saveResult(journal, owner, descriptor, payload); capture()
    } catch (cause) {
      if (!current(token)) return
      if (scopeDenied(cause)) { clearDenied(); error.value = auditServiceErrorKey(cause, 'journalUnavailable') }
      else error.value = 'journalLoadFailed'
    } finally { if (token === generation || !ready.value) { loading.value = false; refreshing.value = false } }
  }
  async function initialize() {
    const token = ++generation
    owner = journalCache.ticket(); verified = false; ready.value = false; result.value = null
    panelOpen.value = false; error.value = ''; validation.value = ''; loading.value = true; stale.value = false
    Object.assign(options, { actor: optionState(), template: optionState() })
    const stored = journalCache.restore(journal, owner)
    if (!stored) {
      const defaults = defaultFilters(journal, new Date(), actor)
      Object.assign(applied, defaults); Object.assign(draft, toDraft(defaults))
      page.value = 1; pageSize.value = 25; searchDraft.value = ''
    }
    try {
      if (!await verifyMonitoringContext(() => current(token))) return
      if (!hasPermission(journalPermission(journal))) throw Object.assign(new Error('Denied'), { status: 403 })
      if (!session.platformContext?.activeStationId) { error.value = 'journalChooseStation'; loading.value = false; return }
      ready.value = true; verified = true
      if (stored) {
        Object.assign(applied, stored.applied); Object.assign(draft, stored.draft)
        page.value = stored.page; pageSize.value = stored.pageSize; searchDraft.value = stored.searchDraft
        result.value = stored.result; stale.value = Boolean(stored.result && !stored.fresh)
      }
      if (stored?.fresh) loading.value = false
      else await load(page.value)
    } catch (cause) {
      if (!current(token)) return
      clearDenied(); error.value = scopeDenied(cause) ? 'journalUnavailable' : 'journalContextFailed'
      loading.value = false
    }
  }
  function apply() {
    try {
      Object.assign(applied, prepareFilters(journal, draft)); validation.value = ''; page.value = 1
      panelOpen.value = false; result.value = null; load(1)
    } catch (cause) { validation.value = cause.message }
  }
  function search() {
    const value = searchDraft.value.trim()
    if (value.length > 120) { validation.value = 'journalInvalidFilter'; return }
    applied.q = value; draft.q = value; page.value = 1; result.value = null; validation.value = ''; load(1)
  }
  function clearDraft() {
    Object.assign(draft, toDraft({ ...defaultFilters(journal), q: applied.q })); validation.value = ''
  }
  function removeDraft(name) {
    if (name === 'period') { const defaults = toDraft(defaultFilters(journal)); draft.from = defaults.from; draft.to = defaults.to }
    else { draft[name] = ''; if (name === 'outcome') draft.errorCategory = '' }
  }
  async function loadOptions(field, number = 1, search = '') {
    if (!ready.value || !panelOpen.value) return
    const state = options[field], ticket = owner, scope = journalScope(session), token = ++state.generation
    state.loading = true; state.error = ''; state.search = search
    try {
      const payload = await (journal === 'runs' ? api : naftaPosAuditApi)(`/admin/report-monitoring-options?${monitoringQuery({ journal, field, stationId: session.platformContext.activeStationId,
        sourceInstanceId: draft.sourceInstanceId, q: search, page: number, pageSize: 50 })}`)
      if (disposed || !journalCache.current(ticket) || journalScope(session) !== scope || token !== state.generation || !panelOpen.value) return
      Object.assign(state, { items: payload.options, page: payload.page, total: payload.total, pageSize: payload.pageSize })
    } catch (cause) {
      if (disposed || token !== state.generation || journalScope(session) !== scope) return
      if (scopeDenied(cause)) { clearDenied(); error.value = auditServiceErrorKey(cause, 'journalUnavailable') }
      else state.error = 'journalOptionsFailed'
    } finally { if (token === state.generation) state.loading = false }
  }
  function toggle() {
    panelOpen.value = !panelOpen.value
    if (panelOpen.value) { loadOptions('actor'); if (journal === 'runs') loadOptions('template') }
  }
  watch(() => journalScope(session), initialize, { immediate: true })
  watch(() => draft.sourceInstanceId, () => { if (panelOpen.value) { loadOptions('actor'); if (journal === 'runs') loadOptions('template') } })
  onBeforeUnmount(() => { capture(); disposed = true; generation++ })
  return { applied, draft, page, pageSize, searchDraft, result, rows, total, totalPages, loading, refreshing, error, validation, stale,
    panelOpen, ready, pending, sources, options, load, apply, search, clearDraft, removeDraft, toggle, loadOptions, clearDenied, retry: initialize }
}
function optionState() { return { items: [], page: 1, pageSize: 50, total: 0, search: '', loading: false, error: '', generation: 0 } }

export function useDiagnostic({ onDenied = () => {} } = {}) {
  const selected = ref(null), detail = ref(null), loading = ref(false), error = ref('')
  let request = 0, disposed = false
  const allowed = computed(() => hasPermission('report_diagnostics.read'))
  function close() { request++; selected.value = null; detail.value = null; error.value = ''; loading.value = false }
  async function open(type, item) {
    if (!allowed.value || !hasPermission(type === 'audit' ? 'audit.read' : 'report_runs.read')) return
    const id = type === 'audit' ? item.id : item.runId
    const expectedRunId = type === 'audit' ? item.payload?.runId : item.runId
    if (!id) return
    const token = ++request, scope = journalScope(session)
    selected.value = { type, id }; detail.value = null; error.value = ''; loading.value = true
    try {
      const value = await (type === 'audit' ? naftaPosAuditApi : api)(`/admin/${type === 'audit' ? 'audit-events' : 'report-runs'}/${encodeURIComponent(id)}/diagnostic`)
      if (!disposed && token === request && scope === journalScope(session) && allowed.value) {
        if (value?.schemaVersion !== 1 || value.run?.runId !== expectedRunId
          || Number(value.run?.stationId) !== Number(session.platformContext?.activeStationId)) {
          error.value = 'diagnosticUnavailable'; return
        }
        detail.value = value
      }
    } catch (cause) {
      if (!disposed && token === request && scope === journalScope(session)) {
        detail.value = null; error.value = auditServiceErrorKey(cause, scopeDenied(cause) ? 'diagnosticForbidden' : 'diagnosticUnavailable')
        if (scopeDenied(cause)) { journalCache.clear(); onDenied(cause, type) }
      }
    } finally { if (token === request) loading.value = false }
  }
  watch(() => journalScope(session), close, { flush: 'sync' })
  onBeforeUnmount(() => { disposed = true; close() })
  return { selected, detail, loading, error, allowed, open, close }
}
